import { AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { ApiTransactionService } from '@services/api-transaction.service';
import { CouponModel } from '@shared/models/CouponModel';
import { CustomerGiveawayRegisterModel } from '@shared/models/CustomerModel';
import { Country, GiveawayProductType, GiveawayType } from '@shared/models/Enums';
import { ScratchModel } from '@shared/models/ScratchModel';

@Component({
    selector: 'app-lucky-scratch-box',
    templateUrl: './lucky-scratch-box.component.html',
    styleUrls: ['./lucky-scratch-box.component.scss']
})
export class LuckyScratchBoxComponent implements AfterViewInit {

    @Input() coupon: CouponModel;
    @Input() selectedScratch: ScratchModel;
    @Input() isWinner: boolean = false;
    @Input() customerId: number;
    @Input() uniqueLinkCode: string = null;
    @Input() country: Country = Country.CR;
    @Input() isNoSession: boolean = false;
    @Input() loyaltyPlanCustomerStickerId: number = null;

    @Output() claimGiveaway: EventEmitter<void> = new EventEmitter<void>();

    @ViewChild('scratcherCanvas') canvasRef!: ElementRef<HTMLCanvasElement>;

    firstScratch = true;
    showClaim = false;
    /** Motivo por el que el API rechazó el registro del premio. */
    claimError: string = null;
    hideCanvas = false;

    // % raspado (0-100) para decidir el reveal.
    scratchPercent = 0;

    ctx!: CanvasRenderingContext2D;
    isScratching = false;

    constructor(public apiTransactionService: ApiTransactionService) { }

    ngAfterViewInit() {
        if (this.canvasRef && this.canvasRef.nativeElement) {
            this.setupCanvas();
        }
    }

    get showReveal(): boolean {
        return this.showClaim && this.isWinner && !!this.coupon && this.scratchPercent > 60;
    }

    onClaimGiveaway() {
        setTimeout(() => this.hideCanvas = true, 200);
        this.claimGiveaway.emit();
    }

    setupCanvas() {
        const canvas = this.canvasRef.nativeElement;
        this.ctx = canvas.getContext('2d');
        canvas.width = canvas.offsetWidth;
        canvas.height = canvas.offsetHeight;

        // Capa plateada clara (similar a la nativa: #C7C7CC) con banda de brillo
        // diagonal para efecto "metalico".
        const base = this.ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
        base.addColorStop(0, '#E5E7EA');
        base.addColorStop(0.45, '#F5F6F8');
        base.addColorStop(0.55, '#F5F6F8');
        base.addColorStop(1, '#B8BBC1');
        this.ctx.fillStyle = base;
        this.ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Rayas diagonales muy sutiles para textura.
        this.ctx.strokeStyle = 'rgba(255, 255, 255, 0.18)';
        this.ctx.lineWidth = 1;
        for (let i = -canvas.height; i < canvas.width; i += 6) {
            this.ctx.beginPath();
            this.ctx.moveTo(i, 0);
            this.ctx.lineTo(i + canvas.height, canvas.height);
            this.ctx.stroke();
        }

        canvas.addEventListener('mousedown', this.startScratch.bind(this));
        canvas.addEventListener('mousemove', this.scratch.bind(this));
        canvas.addEventListener('mouseup', this.stopScratch.bind(this));
        canvas.addEventListener('mouseleave', this.stopScratch.bind(this));

        canvas.addEventListener('touchstart', this.startScratch.bind(this), { passive: false });
        canvas.addEventListener('touchmove', this.scratch.bind(this), { passive: false });
        canvas.addEventListener('touchend', this.stopScratch.bind(this), { passive: false });
    }

    startScratch(event: MouseEvent | TouchEvent) {

        if (this.firstScratch) {
            this.firstScratch = false;

            let customerGiveaway = new CustomerGiveawayRegisterModel({
                scratchId: this.selectedScratch?.id,
                customerId: this.customerId,
                type: GiveawayType.Lotto,
                productType: GiveawayProductType.Coupons,
                productId: this.isWinner ? this.coupon.id : null,
                productName: this.isWinner ? this.coupon.name : null,
                deleteFromEnv: this.isWinner ? true : false
            });

            if (this.uniqueLinkCode?.length > 0) customerGiveaway.uniqueLinkCode = this.uniqueLinkCode;
            if (this.loyaltyPlanCustomerStickerId != null) customerGiveaway.loyaltyPlanCustomerStickerId = this.loyaltyPlanCustomerStickerId;

            const call = this.isNoSession
                ? this.apiTransactionService.withoutSessionPostCustomerGiveaway(customerGiveaway, this.country)
                : this.apiTransactionService.postCustomerGiveaway(customerGiveaway, this.country);

            call.then(res => {

                this.claimError = null;
                if (this.isWinner) this.showClaim = true;

            }).catch((err: any) => {

                // El API manda el motivo en el body. Sin esto el usuario ganaba,
                // no aparecía el botón y no había ninguna explicación en pantalla.
                this.showClaim = false;
                this.claimError = err?.error?.Message || err?.error?.message
                    || 'No pudimos registrar tu premio. Intentá de nuevo en unos minutos.';

                console.error('customer-giveaway/create/v1 falló', err);
            });
        }

        event.preventDefault();
        this.isScratching = true;
        this.scratch(event);
    }

    scratch(event: MouseEvent | TouchEvent) {

        if (!this.isScratching) return;
        event.preventDefault();

        let x: number, y: number;
        const rect = this.canvasRef.nativeElement.getBoundingClientRect();
        if (event instanceof MouseEvent) {
            x = event.clientX - rect.left;
            y = event.clientY - rect.top;
        } else if (event.touches) {
            x = event.touches[0].clientX - rect.left;
            y = event.touches[0].clientY - rect.top;
        }

        this.ctx.globalCompositeOperation = 'destination-out';
        this.ctx.beginPath();
        this.ctx.arc(x, y, 24, 0, Math.PI * 2);
        this.ctx.fill();

        this.updateScratchPercent();
    }

    stopScratch() {
        this.isScratching = false;
    }

    private updateScratchPercent() {
        const canvas = this.canvasRef.nativeElement;
        const step = 20;
        const w = canvas.width;
        const h = canvas.height;
        const data = this.ctx.getImageData(0, 0, w, h).data;
        let cleared = 0;
        let total = 0;
        for (let y = 0; y < h; y += step) {
            for (let x = 0; x < w; x += step) {
                const idx = (y * w + x) * 4 + 3;
                if (data[idx] === 0) cleared++;
                total++;
            }
        }
        const pct = (cleared / total) * 100;
        if (pct > this.scratchPercent) this.scratchPercent = pct;
    }
}
